import type { ReactNode } from 'react';
import { NavLink, useMatch } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Info, KeyRound, LogOut, TriangleAlert, Users } from 'lucide-react';
import { api, type Health, type MeResponse } from '@/lib/api';
import { useLogout } from '@/lib/session';
import { cn } from '@/lib/utils';

const navClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'font-heading flex h-10 items-center gap-2 rounded-lg px-3 text-xs font-semibold tracking-wide uppercase transition-colors hover:bg-white/12',
    isActive && 'bg-white/18',
  );

/** Gabarit des pages connectées : en-tête, navigation, bandeau « données simulées ». */
export default function Layout({ session, children }: { session: MeResponse; children: ReactNode }) {
  const logout = useLogout();
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api<Health>('/api/health'),
    staleTime: 5 * 60 * 1000,
  });
  // Les prix font foi : simulés tant que le Tomcat n'est pas branché.
  const simulated = health.data?.tomcat === 'mock';
  const partNumberOnly = health.data?.tomcat === 'configured' && health.data.catalogueMode === 'mock';
  // La commande (produits + panier côte à côte) prend toute la largeur de la tablette.
  const wide = useMatch('/commandes/:id') !== null;

  return (
    <div className="flex min-h-full flex-col">
      <header className="bg-primary text-primary-foreground sticky top-0 z-20">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
          <NavLink to="/" className="flex items-center gap-3">
            <img src="/logo-small.png" alt="Super" className="h-9 w-auto" />
            <span className="font-heading text-lg font-extrabold tracking-wider uppercase">Reps</span>
          </NavLink>
          <nav className="flex items-center gap-1">
            {session.user.role === 'ADMIN' && (
              <NavLink to="/admin/comptes" className={navClass}>
                <Users className="size-4" />
                <span className="hidden sm:inline">Comptes</span>
              </NavLink>
            )}
            <NavLink to="/compte" className={navClass} title="Mon mot de passe">
              <KeyRound className="size-4" />
              <span className="hidden sm:inline">{session.user.fullName}</span>
            </NavLink>
            <button onClick={() => logout.mutate()} className={navClass({ isActive: false })}>
              <LogOut className="size-4" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </nav>
        </div>
        <div aria-hidden className="damier-strip" />
      </header>

      {simulated && (
        <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-sm text-amber-900">
          <TriangleAlert className="size-4 shrink-0" />
          Prix simulés : catalogue et prix fictifs, en attendant l'accès à Epicor.
          Ne pas transmettre ces commandes.
        </div>
      )}
      {partNumberOnly && (
        <div className="bg-accent text-accent-foreground flex items-center gap-2 px-4 py-2 text-sm">
          <Info className="size-4 shrink-0" />
          Prix Epicor réels. Le catalogue complet n'est pas encore en ligne : cherchez par numéro de pièce.
        </div>
      )}

      <main className={cn('mx-auto w-full flex-1 space-y-4 p-4 sm:p-6', wide ? 'max-w-screen-2xl' : 'max-w-5xl')}>
        {children}
      </main>
    </div>
  );
}
